<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
  >
    <!-- @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)" -->
    <!-- main menu header-->
    <div class="navbar-header expanded" style="padding: 0.3rem 0px">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link to="/">
              <span>
                <b-img
                  style="height: 80px; width: 80px; object-fit: contain"
                  :src="logoPreviewUrl"
                  alt="logo"
                  referrerpolicy="no-referrer"
                  @error="onLogoPreviewError"
                />
              </span>
              <!-- <h2 class="brand-text">
                {{ appName }}
              </h2> -->
            </b-link>
          </li>

          <!-- Toggler Button -->
          <!-- <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li> -->
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <ul
      ref="menuScroll"
      class="main-menu-content scroll-area"
      @scroll="onMenuScroll"
      @mouseenter="onMenuMouseEnter"
      @mouseleave="onMenuMouseLeave"
      @mouseover="onNavItemOver"
      @mouseout="onNavItemOut"
    >
      <vertical-nav-menu-items
        :items="$store.state.menu"
        class="navigation navigation-main"
      />
    </ul>
    <portal to="nav-tooltips">
      <div
        v-if="navTooltip.visible"
        class="nav-hover-label"
        :style="{ top: navTooltip.top + 'px', left: navTooltip.left + 'px' }"
      >
        {{ navTooltip.label }}
      </div>
    </portal>
    <div
      v-show="scrollbarVisible"
      class="menu-scroll-indicator"
      aria-hidden="true"
    >
      <div
        class="menu-scroll-thumb"
        :style="{ transform: `translateY(${scrollThumbTop}px)` }"
      />
    </div>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from "@/navigation/vertical";
import { BLink, BImg } from "bootstrap-vue";
import { provide, computed, ref } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { $themeConfig } from "@themeConfig";
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue";
import useVerticalNavMenu from "./useVerticalNavMenu";
import { getMediaPreviewUrl, handleDrivePreviewError } from "@/utils/gdriveUpload";

export default {
  components: {
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false);

    provide("isMouseHovered", isMouseHovered);

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    );

    // App Name
    const { appName, appLogoImage } = $themeConfig.app;

    return {
      navMenuItems,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    };
  },
  computed: {
    logoPreviewUrl() {
      const url = this.$store.state.appConfig.appLogoImage;
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
  },
  data() {
    return {
      scrollbarVisible: false,
      scrollThumbTop: 0,
      scrollHideTimer: null,
      navTooltip: {
        visible: false,
        label: "",
        top: 0,
        left: 0,
      },
    };
  },
  beforeDestroy() {
    if (this.scrollHideTimer) clearTimeout(this.scrollHideTimer);
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollIndicator();
      this.scheduleScrollbarHide();
    });
  },
  methods: {
    updateScrollIndicator() {
      const el = this.$refs.menuScroll;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) {
        this.scrollbarVisible = false;
        this.scrollThumbTop = 0;
        return;
      }
      // Keep the thumb short (44px) and move it along the menu height.
      const thumbHeight = 44;
      const trackHeight = Math.max(0, el.clientHeight - 16);
      const maxTop = Math.max(0, trackHeight - thumbHeight);
      this.scrollThumbTop = (el.scrollTop / maxScroll) * maxTop;
      this.scrollbarVisible = true;
    },
    scheduleScrollbarHide() {
      if (this.scrollHideTimer) clearTimeout(this.scrollHideTimer);
      this.scrollHideTimer = setTimeout(() => {
        this.scrollbarVisible = false;
      }, 900);
    },
    onMenuScroll(event) {
      const target = event && event.target;
      this.shallShadowBottom = !!target && target.scrollTop > 0;
      this.navTooltip.visible = false;
      this.updateScrollIndicator();
      this.scheduleScrollbarHide();
    },
    onMenuMouseEnter() {
      const el = this.$refs.menuScroll;
      if (el && el.scrollHeight > el.clientHeight) {
        this.updateScrollIndicator();
      }
    },
    onMenuMouseLeave() {
      this.navTooltip.visible = false;
      this.scheduleScrollbarHide();
    },
    onNavItemOver(event) {
      if (!this.isVerticalMenuCollapsed) return;
      const target = event && event.target;
      const link =
        target && target.closest ? target.closest("[data-nav-label]") : null;
      if (!link) return;
      const label = link.getAttribute("data-nav-label");
      if (!label) return;
      const rect = link.getBoundingClientRect();
      this.navTooltip = {
        visible: true,
        label,
        top: rect.top + rect.height / 2,
        left: rect.right + 12,
      };
    },
    onNavItemOut(event) {
      const target = event && event.target;
      const link =
        target && target.closest ? target.closest("[data-nav-label]") : null;
      if (!link) return;
      const related = event.relatedTarget;
      if (related && related.closest && related.closest("[data-nav-label]")) {
        return;
      }
      this.navTooltip.visible = false;
    },
    onLogoPreviewError(event) {
      const url = this.$store.state.appConfig.appLogoImage;
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },
  },
};
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

/* Collapsed-sidebar hover label, teleported to body so it is never clipped. */
.nav-hover-label {
  position: fixed;
  z-index: 10050;
  transform: translateY(-50%);
  padding: 6px 12px;
  border-radius: 6px;
  background: #343a40;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.nav-hover-label::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -5px;
  transform: translateY(-50%);
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 5px solid #343a40;
}
</style>
<style scoped="css">
.navbar-header {
  height: 6rem;
}

/* Native scrolling is much cheaper than a JS scrollbar during route changes. */
.main-menu-content {
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none;
}

.main-menu-content::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* Short custom thumb: a native scrollbar thumb grows with the content and
   looked like a full-height bar on this long menu. */
.menu-scroll-indicator {
  position: absolute;
  top: 6rem;
  bottom: 0.5rem;
  right: 2px;
  width: 4px;
  z-index: 4;
  pointer-events: none;
}

.menu-scroll-thumb {
  width: 100%;
  height: 44px;
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.45);
  transition: opacity 0.2s ease;
}
</style>
